import React from 'react';
import styled from 'styled-components';
import { Field, FieldProps, Formik, FormikHelpers, FormikProps } from 'formik';
import { number, object, string } from 'yup';
import { DefaultOption, Grid } from '@inplayer-org/inplayer-ui';

// data
import accessCurrencies from 'data/accessCurrencies';

// components
import { BlueTypography } from 'components/SharedStyledElements';
import StyledContainer from 'components/StyledContainer';
import Input from 'components/Fields/Input';
import FieldError from 'components/Fields/FieldError';
import Select from 'components/Fields/Select';

import AccordionFooter from 'components/AccordionFooter';
import { CustomButton } from 'pages/MerchantPages/Assets/Price/containers/PriceOptions';
import { Container } from 'pages/MerchantPages/Assets/components/Donations/Donations';

const { Cell } = Grid;

export interface FormValues {
  id?: number;
  description?: string;
  amount: string | number;
  currency: string;
}

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

interface Props {
  handleBackToDonations: (value: boolean) => () => void;
  onSubmit: (data: FormValues) => void;
  isEditing: boolean;
  donationInEdit: FormValues | null;
  closePanel: () => void;
}

const validationSchema = object().shape({
  description: string().required('*Required'),
  amount: number().required('*Required'),
  currency: string().required('*Required'),
});

const CreateDonationForm: React.FC<Props> = ({
  handleBackToDonations,
  onSubmit,
  isEditing,
  donationInEdit,
  closePanel,
}) => {
  const initialValues: FormValues = {
    description: '',
    amount: '',
    currency: '',
  };

  const inEditValues: FormValues = {
    description: donationInEdit?.description || '',
    amount: donationInEdit?.amount!,
    currency: donationInEdit?.currency!,
  };

  const defaultOption: DefaultOption = {
    displayName: 'Donation currency',
    disabled: true,
  };

  const onFormSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    await onSubmit(values);
    setSubmitting(false);
  };
  return (
    <Formik
      onSubmit={onFormSubmit}
      enableReinitialize
      initialValues={isEditing ? inEditValues : initialValues}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting, isValid, dirty }: FormikProps<FormValues>) => (
        <StyledForm onSubmit={handleSubmit}>
          <Container>
            <CustomButton buttonModifiers={['buttonLink']} type="button" onClick={handleBackToDonations(false)}>
              &larr; back
            </CustomButton>

            <StyledContainer margin="2rem 0 0 0" rowGap="1rem" columns={12}>
              <Cell width={12}>
                <BlueTypography variant="h6">Donation description</BlueTypography>
              </Cell>
              <Cell width={12}>
                <Field
                  tag="text_input_donation_description"
                  name="description"
                  type="text"
                  component={Input}
                  placeholder="Donation description"
                />
                <FieldError name="description" />
              </Cell>
              <Cell width={12}>
                <BlueTypography variant="h6">Donation amount</BlueTypography>
              </Cell>
              <Cell width={4}>
                <Field
                  tag="text_input_donation_amount"
                  name="amount"
                  type="number"
                  component={Input}
                  placeholder="Donation amount"
                />
                <FieldError name="amount" />
              </Cell>

              <Cell width={4}>
                <Field tag="dropdown_currency" name="currency">
                  {({ field, form, meta }: FieldProps) => (
                    <Select
                      field={field}
                      form={form}
                      meta={meta}
                      value={field.value}
                      defaultOption={defaultOption}
                      options={accessCurrencies}
                    />
                  )}
                </Field>
                <FieldError name="currency" />
              </Cell>
            </StyledContainer>
          </Container>
          <AccordionFooter
            handleCancel={closePanel}
            buttonType="submit"
            disabled={isSubmitting || !isValid || !dirty}
          />
        </StyledForm>
      )}
    </Formik>
  );
};

export default CreateDonationForm;
